/**
 * Typed API client.
 *
 * Mirrors the OpenAPI contract at /openapi.json. Every request carries a Firebase ID token as
 * a bearer header — no cookies — so a native client uses these endpoints identically.
 */
import { getIdToken } from "./firebase";

const BASE = import.meta.env.VITE_API_URL ?? "/api";

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  // The SDK refreshes this when it's close to expiry, so fetching per request is cheap and
  // means a tab left open overnight never sends a stale token.
  const token = await getIdToken();

  const headers: Record<string, string> = {};
  if (options.body) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${BASE}${path}`, {
    method: options.method ?? "GET",
    headers,
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as { error?: { code: string; message: string; details?: unknown } })
      ?.error;
    throw new ApiError(
      error?.code ?? "unknown",
      error?.message ?? "Something went wrong.",
      response.status,
      error?.details,
    );
  }

  return payload as T;
}

/* Types ------------------------------------------------------------------- */

export interface User {
  id: string;
  email: string | null;
  displayName: string;
  avatarColor: string;
  photoUrl: string | null;
  isSuperAdmin: boolean;
  createdAt: string;
}

export interface ProfileStatus {
  signedIn: boolean;
  /** Token is valid but no username chosen yet — show the username step. */
  needsProfile: boolean;
  email: string | null;
  suggestedDisplayName: string | null;
}

/** The rules endpoints accept a deep partial — send only what you're changing. */
export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export interface CompetitionRules {
  elimination: {
    drawCountsAs: "loss" | "win" | "void";
    lives: number;
    missedPick: "eliminate" | "auto_assign" | "carry_forward";
  };
  picks: {
    teamReuse: "never" | "reset_on_exhaustion" | "unlimited";
    reveal: "after_deadline" | "immediately" | "after_settlement";
    deadlineOffsetMinutes: number;
    teamPlaysTwice: "first_fixture" | "must_win_both";
  };
  wipeout: "full_reset" | "revive_round" | "split" | "no_winner";
  postponedFixture: "void_survive" | "loss" | "hold_round";
}

export interface Round {
  id: string;
  roundNumber: number;
  status: "scheduled" | "open" | "locked" | "settled" | "void";
  deadlineAt: string;
  windowStart: string;
  windowEnd: string;
  settledAt: string | null;
  fixtureCount: number;
}

export interface Competition {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  /** Only present for admins — it's the only way into the competition. */
  inviteCode?: string;
  role: "admin" | "player";
  isOwner: boolean;
  /** A platform admin overseeing this competition without owning or having joined it. */
  isPlatformAdmin: boolean;
  isJoinable: boolean;
  status: "draft" | "running" | "complete";
  league: { id: string; name: string };
  season: { id: string; label: string };
  rules: CompetitionRules;
  rulesLocked: boolean;
  ruleSummary: string[];
  playersAlive: number;
  playersTotal: number;
  currentRoundNumber: number | null;
  winner: { entryId: string; displayName: string } | null;
  sharedWinners: Array<{ entryId: string; displayName: string }>;
  createdAt: string;
}

export interface CompetitionDetail extends Competition {
  yourEntry: {
    id: string;
    status: "alive" | "out";
    livesRemaining: number;
    eliminatedRound: number | null;
  } | null;
  currentRound: Round | null;
  members: Array<{
    entryId: string;
    userId: string;
    displayName: string;
    avatarColor: string;
    role: "admin" | "player";
    status: "alive" | "out";
    livesRemaining: number;
    joinedAt: string;
    isOwner: boolean;
    isSelf: boolean;
    /** A player the admin created as a stand-in for someone not using the app yet. */
    isManaged: boolean;
    /** Only present for admins, and only while unclaimed. Build a link with it as `/claim/:token`. */
    claimToken: string | null;
  }>;
}

/** What a share link resolves to, before you commit to joining it. */
export interface InvitePreview {
  id: string;
  name: string;
  description: string | null;
  league: string;
  season: string;
  playersTotal: number;
  status: "draft" | "running" | "complete";
  alreadyMember: boolean;
  canJoin: boolean;
  reason?:
    | "already_member"
    | "removed"
    | "competition_complete"
    | "entries_closed"
    | "entry_closed";
}

export interface ResultsGrid {
  rounds: Array<{
    id: string;
    roundNumber: number;
    status: "scheduled" | "open" | "locked" | "settled" | "void";
  }>;
  entries: Array<{
    entryId: string;
    displayName: string;
    avatarColor: string;
    status: "alive" | "out";
    eliminatedRoundId: string | null;
  }>;
  cells: Array<{
    entryId: string;
    roundId: string;
    hasPicked: boolean;
    team: { name: string; shortName: string; crestUrl: string | null } | null;
    outcome: "pending" | "win" | "loss" | "void" | null;
    autoAssigned: boolean | null;
  }>;
}

/**
 * The polling contract behind live results.
 *
 * `version` is opaque and changes whenever anything that would change the grid changes — a
 * score, a pick's outcome, a round settling, the competition being won. Clients compare it
 * against the last one they saw and re-fetch the real data only when it moves, rather than
 * re-fetching the grid, the leaderboard and every round's picks on a timer.
 *
 * The rest is there so the client can choose a sensible cadence for itself: poll hard while
 * matches are actually being played, and barely at all the rest of the week.
 */
export interface LiveState {
  version: string;
  competitionStatus: "draft" | "running" | "complete";
  roundInPlay: boolean;
  liveFixtures: number;
  pendingFixtures: number;
  nextKickoffAt: string | null;
  settledRounds: number;
  playersAlive: number;
  lastSyncedAt: string | null;
  serverTime: string;
}

export interface RefreshOutcome {
  /** False when the server's per-league rate limit declined to re-ask the provider. */
  synced: boolean;
  reason?: "cooldown" | "nothing_in_play" | "no_active_league";
  fixturesUpdated: number;
  picksDecided: number;
  roundsSettled: number;
  errors: string[];
  state: LiveState;
}

export interface SelectableTeam {
  teamId: string;
  name: string;
  shortName: string;
  crestUrl: string | null;
  fixtureId: string;
  kickoffAt: string;
  isHome: boolean;
  opponentName: string;
  opponentCrestUrl: string | null;
  used: boolean;
  playsTwice: boolean;
}

export interface Pick {
  entryId: string;
  displayName: string;
  avatarColor: string;
  status: "alive" | "out";
  hasPicked: boolean;
  isSelf: boolean;
  team: { id: string; name: string | null; shortName: string | null; crestUrl: string | null } | null;
  outcome: "pending" | "win" | "loss" | "void" | null;
  autoAssigned: boolean | null;
  /** Plain-English settlement explanation. Null until settled, or while the pick is masked. */
  settlementReason: string | null;
}

export interface LeaderboardRow {
  entryId: string;
  displayName: string;
  avatarColor: string;
  status: "alive" | "out";
  livesRemaining: number;
  eliminatedRound: number | null;
  teamsUsed: number;
}

export interface League {
  id: string;
  name: string;
  country: string | null;
  currentSeason: { id: string; label: string } | null;
}

export interface AdminOverview {
  users: number;
  competitions: number;
  runningCompetitions: number;
  aliveEntries: number;
  leagues: number;
  failedSyncs24h: number;
}

/**
 * A Workers invocation can only afford a few leagues' worth of subrequests, so a sync of
 * "everything" is bounded and says what it didn't reach. Run it again to continue — the
 * least-recently-synced lead the order.
 */
export interface SyncOutcome {
  leagues: Array<{
    leagueName: string;
    providerKey: string | null;
    usedFallback: boolean;
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
  }>;
  remaining: number;
  /** The invocation ran out of subrequests part-way. What's reported still happened. */
  budgetExhausted: boolean;
}

export interface SyncRun {
  id: string;
  leagueName: string | null;
  providerKey: string | null;
  trigger: "cron" | "manual";
  status: "running" | "success" | "partial" | "failed";
  usedFallback: boolean;
  fixturesCreated: number;
  fixturesUpdated: number;
  fixturesSkipped: number;
  errors: unknown[];
  startedAt: string;
  finishedAt: string | null;
}

export interface Provider {
  id: string;
  key: string;
  name: string;
  adapter: string;
  config: Record<string, unknown>;
  isActive: boolean;
  priority: number;
  rateLimitPerMin: number;
}

export interface AdminFixture {
  id: string;
  kickoffAt: string;
  status: "scheduled" | "in_play" | "finished" | "postponed" | "abandoned" | "cancelled";
  leagueName?: string;
  seasonLabel?: string;
  homeTeam: { id: string; name: string; crestUrl: string | null };
  awayTeam: { id: string; name: string; crestUrl: string | null };
  homeScore: number | null;
  awayScore: number | null;
  source: string;
  /** Fields an admin has hand-edited — these survive every later sync. */
  manualFields: string[];
  locked: boolean;
}

export interface FixtureFilters {
  leagueId?: string;
  seasonId?: string;
  teamId?: string;
  status?: string;
  source?: "manual" | "provider";
  from?: string;
  to?: string;
  search?: string;
  limit?: number;
  offset?: number;
  order?: "asc" | "desc";
}

export interface AdminLeague {
  id: string;
  name: string;
  country: string | null;
  providerSlug: string;
  isActive: boolean;
  seasons: Array<{ id: string; label: string; isCurrent: boolean; fixtureCount: number }>;
}

export interface AdminTeam {
  id: string;
  name: string;
  shortName: string;
  crestUrl: string | null;
  aliases: string[];
}

export interface AuditEntry {
  id: string;
  action: string;
  target: string | null;
  payload: Record<string, unknown>;
  reason: string | null;
  createdAt: string;
  actor: { id: string; displayName: string } | null;
}

export interface AdminUser {
  id: string;
  email: string | null;
  displayName: string;
  avatarColor: string;
  photoUrl: string | null;
  isSuperAdmin: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  competitionsCount: number;
}

export interface AdminUserDetail extends AdminUser {
  competitions: Array<{
    id: string;
    slug: string;
    name: string;
    status: "draft" | "running" | "complete";
    isOwner: boolean;
    role: "admin" | "player";
    entryStatus: "alive" | "out";
    livesRemaining: number;
    joinedAt: string;
  }>;
}

export interface SheetMirrorStatus {
  enabled: boolean;
  spreadsheetId: string | null;
  spreadsheetUrl: string | null;
  /** GOOGLE_SERVICE_ACCOUNT is set on the Worker. The value itself is never sent to a client. */
  credentialsPresent: boolean;
  competitions: Array<{
    competitionId: string;
    name: string;
    tabTitle: string | null;
    syncedAt: string | null;
    lastError: string | null;
    failureCount: number;
  }>;
}

export interface PollSchedule {
  mode: "after_matches" | "daily";
  /** UK hour, 0–23. */
  dailyHour: number;
  nextDueAt: string | null;
}

export interface SheetMirrorOutcome {
  attempted: number;
  written: number;
  unchanged: number;
  failed: number;
  errors: string[];
}

export interface AdminCompetition {
  id: string;
  slug: string;
  name: string;
  status: "draft" | "running" | "complete";
  isJoinable: boolean;
  ownerId: string;
  ownerDisplayName: string;
  leagueName: string;
  seasonLabel: string;
  playersAlive: number;
  playersTotal: number;
  createdAt: string;
}

/* Endpoints --------------------------------------------------------------- */

export const api = {
  auth: {
    me: () => request<User>("/auth/me"),
    profileStatus: () => request<ProfileStatus>("/auth/profile-status"),
    createProfile: (displayName: string) =>
      request<User>("/auth/profile", { method: "POST", body: { displayName } }),
    updateProfile: (body: { displayName?: string; avatarColor?: string }) =>
      request<User>("/auth/me", { method: "PATCH", body }),
    claim: (token: string) =>
      request<{ user: User; skippedCompetitionIds: string[] }>("/auth/claim", {
        method: "POST",
        body: { token },
      }),
  },

  competitions: {
    list: () => request<Competition[]>("/competitions"),
    get: (id: string) => request<CompetitionDetail>(`/competitions/${id}`),
    create: (body: {
      name: string;
      description?: string;
      leagueId: string;
      rules?: DeepPartial<CompetitionRules>;
    }) => request<Competition>("/competitions", { method: "POST", body }),
    invitePreview: (code: string) =>
      request<InvitePreview>(`/competitions/invite/${encodeURIComponent(code.trim().toUpperCase())}`),
    join: (inviteCode: string) =>
      request<Competition>("/competitions/join", {
        method: "POST",
        body: { inviteCode: inviteCode.trim().toUpperCase() },
      }),
    update: (
      id: string,
      body: {
        name?: string;
        description?: string | null;
        regenerateInviteCode?: boolean;
        isJoinable?: boolean;
      },
    ) => request<Competition>(`/competitions/${id}`, { method: "PATCH", body }),
    removeMember: (id: string, userId: string, reason: string) =>
      request<{ ok: boolean }>(`/competitions/${id}/members/${userId}`, {
        method: "DELETE",
        body: { reason },
      }),
    updateMemberRole: (id: string, userId: string, role: "admin" | "player") =>
      request<{ ok: boolean }>(`/competitions/${id}/members/${userId}`, {
        method: "PATCH",
        body: { role },
      }),
    transferOwnership: (id: string, userId: string) =>
      request<{ ok: boolean }>(`/competitions/${id}/transfer-ownership`, {
        method: "POST",
        body: { userId },
      }),
    delete: (id: string, confirmation: string) =>
      request<{ ok: boolean }>(`/competitions/${id}`, {
        method: "DELETE",
        body: { confirmation },
      }),
    rounds: (id: string) => request<Round[]>(`/competitions/${id}/rounds`),
    teams: (id: string, roundId: string) =>
      request<SelectableTeam[]>(`/competitions/${id}/rounds/${roundId}/teams`),
    pick: (id: string, roundId: string, teamId: string) =>
      request<{ teamId: string; fixtureId: string; kickoffAt: string }>(
        `/competitions/${id}/rounds/${roundId}/pick`,
        { method: "PUT", body: { teamId } },
      ),
    picks: (id: string, roundId: string) =>
      request<Pick[]>(`/competitions/${id}/rounds/${roundId}/picks`),
    fixtures: (id: string, roundId: string) =>
      request<
        Array<{
          id: string;
          kickoffAt: string;
          status: string;
          homeTeam: { id: string; name: string; crestUrl: string | null };
          awayTeam: { id: string; name: string; crestUrl: string | null };
          homeScore: number | null;
          awayScore: number | null;
        }>
      >(`/competitions/${id}/rounds/${roundId}/fixtures`),
    leaderboard: (id: string) => request<LeaderboardRow[]>(`/competitions/${id}/leaderboard`),
    updateRules: (id: string, rules: DeepPartial<CompetitionRules>) =>
      request<Competition>(`/competitions/${id}/rules`, { method: "PATCH", body: rules }),
    settle: (id: string, roundId: string, body: { force: boolean; reason?: string }) =>
      request<{
        settled: boolean;
        reason?: string;
        survived: number;
        eliminated: number;
        wipeout: boolean;
        competitionComplete: boolean;
      }>(`/competitions/${id}/rounds/${roundId}/settle`, { method: "POST", body }),
    revive: (id: string, entryId: string, reason: string) =>
      request<{ ok: boolean }>(`/competitions/${id}/entries/${entryId}/revive`, {
        method: "POST",
        body: { reason },
      }),
    addManagedPlayer: (id: string, displayName: string) =>
      request<{ entryId: string; userId: string; claimToken: string }>(
        `/competitions/${id}/members/managed`,
        { method: "POST", body: { displayName } },
      ),
    reissueClaimLink: (id: string, userId: string) =>
      request<{ claimToken: string }>(`/competitions/${id}/members/${userId}/claim-link`, {
        method: "POST",
      }),
    adminTeams: (id: string, roundId: string, entryId: string) =>
      request<SelectableTeam[]>(`/competitions/${id}/rounds/${roundId}/entries/${entryId}/teams`),
    adminPick: (id: string, roundId: string, entryId: string, teamId: string) =>
      request<{ teamId: string; fixtureId: string; kickoffAt: string }>(
        `/competitions/${id}/rounds/${roundId}/entries/${entryId}/pick`,
        { method: "PUT", body: { teamId } },
      ),
    resultsGrid: (id: string) => request<ResultsGrid>(`/competitions/${id}/results-grid`),
    /** Cheap. Poll this; re-fetch everything else only when `version` changes. */
    live: (id: string, signal?: AbortSignal) =>
      request<LiveState>(`/competitions/${id}/live`, signal ? { signal } : {}),
    /** Pull results now and settle anything ready, rather than waiting for the next poll. */
    refresh: (id: string) =>
      request<RefreshOutcome>(`/competitions/${id}/refresh`, { method: "POST" }),
  },

  leagues: {
    list: () => request<League[]>("/leagues"),
  },

  admin: {
    overview: () => request<AdminOverview>("/admin/overview"),
    providers: () => request<{ providers: Provider[]; adapters: string[] }>("/admin/providers"),
    createProvider: (body: Record<string, unknown>) =>
      request<Provider>("/admin/providers", { method: "POST", body }),
    updateProvider: (id: string, body: Record<string, unknown>) =>
      request<Provider>(`/admin/providers/${id}`, { method: "PATCH", body }),
    testProvider: (id: string, body: { slug: string; from?: string; to?: string }) =>
      request<{ ok: boolean; count: number; sample: Record<string, unknown>[]; error?: string }>(
        `/admin/providers/${id}/test`,
        { method: "POST", body },
      ),
    sync: (body: { leagueId?: string; resultsOnly?: boolean; maxLeagues?: number }) =>
      request<SyncOutcome>("/admin/sync", { method: "POST", body }),
    syncRuns: (limit = 50) => request<SyncRun[]>(`/admin/sync-runs?limit=${limit}`),
    audit: (limit = 100) => request<AuditEntry[]>(`/admin/audit?limit=${limit}`),
    importPreview: (body: { seasonId: string; format: "csv" | "json"; payload: string }) =>
      request<ImportPreview>("/admin/import/preview", { method: "POST", body }),
    importApply: (body: {
      seasonId: string;
      format: "csv" | "json";
      payload: string;
      teamMappings?: Record<string, string>;
    }) => request<ImportPreview>("/admin/import/apply", { method: "POST", body }),
    fixtures: (filters: FixtureFilters = {}) => {
      const query = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== "") query.set(key, String(value));
      }
      return request<{
        total: number;
        limit: number;
        offset: number;
        fixtures: AdminFixture[];
      }>(`/admin/fixtures?${query.toString()}`);
    },
    updateFixture: (id: string, body: Record<string, unknown>) =>
      request<AdminFixture>(`/admin/fixtures/${id}`, { method: "PATCH", body }),
    allLeagues: () => request<AdminLeague[]>("/admin/leagues"),
    /** Repoint a league at another provider, rename it, or take it out of the rotation. */
    updateLeague: (
      id: string,
      body: { name?: string; providerSlug?: string; isActive?: boolean },
    ) => request<{ ok: boolean }>(`/admin/leagues/${id}`, { method: "PATCH", body }),
    teams: (leagueId?: string) =>
      request<AdminTeam[]>(`/admin/teams${leagueId ? `?leagueId=${leagueId}` : ""}`),
    users: (filters: { search?: string; limit?: number; offset?: number } = {}) => {
      const query = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== "") query.set(key, String(value));
      }
      return request<{ total: number; limit: number; offset: number; users: AdminUser[] }>(
        `/admin/users?${query.toString()}`,
      );
    },
    user: (id: string) => request<AdminUserDetail>(`/admin/users/${id}`),
    updateUser: (
      id: string,
      body: { displayName?: string; avatarColor?: string; reason?: string },
    ) => request<AdminUser>(`/admin/users/${id}`, { method: "PATCH", body }),
    setSuperAdmin: (id: string, grant: boolean) =>
      request<{ ok: boolean }>(`/admin/users/${id}/super-admin`, {
        method: "POST",
        body: { grant },
      }),
    pollSchedule: () => request<PollSchedule>("/admin/poll-schedule"),
    savePollSchedule: (body: Omit<PollSchedule, "nextDueAt">) =>
      request<PollSchedule>("/admin/poll-schedule", { method: "PUT", body }),
    sheets: () => request<SheetMirrorStatus>("/admin/sheets"),
    saveSheets: (body: { enabled: boolean; spreadsheet?: string }) =>
      request<{ enabled: boolean; spreadsheetId: string | null; spreadsheetUrl: string | null }>(
        "/admin/sheets",
        { method: "PUT", body },
      ),
    syncSheets: (force = false) =>
      request<SheetMirrorOutcome>("/admin/sheets/sync", { method: "POST", body: { force } }),
    allCompetitions: (
      filters: {
        search?: string;
        status?: "draft" | "running" | "complete";
        ownerId?: string;
        limit?: number;
        offset?: number;
      } = {},
    ) => {
      const query = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== "") query.set(key, String(value));
      }
      return request<{
        total: number;
        limit: number;
        offset: number;
        competitions: AdminCompetition[];
      }>(`/admin/competitions?${query.toString()}`);
    },
  },
};

export interface ImportPreview {
  batchId: string | null;
  dryRun: boolean;
  created: number;
  updated: number;
  unchanged: number;
  unmatched: Array<{ row: Record<string, unknown>; reason: string }>;
  changes: Array<{
    fixtureId: string | null;
    description: string;
    before: Record<string, unknown> | null;
    after: Record<string, unknown>;
  }>;
}
